import React from 'react';
import {
  Box,
  CircularProgress,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';

const DEFAULT_MIN_WIDTH = 80;
const DEFAULT_MAX_WIDTH = 800;
const DEFAULT_WIDTH = 160;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function normalizeColumn(column) {
  const minWidth = Number.isFinite(column?.minWidth)
    ? column.minWidth
    : DEFAULT_MIN_WIDTH;
  const maxWidth = Number.isFinite(column?.maxWidth)
    ? column.maxWidth
    : DEFAULT_MAX_WIDTH;
  const defaultWidth = Number.isFinite(column?.defaultWidth)
    ? clamp(column.defaultWidth, minWidth, maxWidth)
    : clamp(DEFAULT_WIDTH, minWidth, maxWidth);

  return {
    ...column,
    minWidth,
    maxWidth,
    defaultWidth,
  };
}

function readStoredWidths(storageKey) {
  if (!storageKey || typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writeStoredWidths(storageKey, value) {
  if (!storageKey || typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Ignore localStorage failures.
  }
}

export function useResizableColumns(columns, options = {}) {
  const { storageKey } = options;

  const normalizedColumns = React.useMemo(() => {
    if (!Array.isArray(columns)) {
      return [];
    }
    return columns
      .filter((column) => column && column.key)
      .map(normalizeColumn);
  }, [columns]);

  const columnsByKey = React.useMemo(() => {
    const map = new Map();
    normalizedColumns.forEach((column) => {
      map.set(column.key, column);
    });
    return map;
  }, [normalizedColumns]);

  const buildInitialWidths = React.useCallback(() => {
    const stored = readStoredWidths(storageKey);
    const next = {};
    normalizedColumns.forEach((column) => {
      const storedWidth = Number(stored?.[column.key]);
      if (Number.isFinite(storedWidth)) {
        next[column.key] = clamp(storedWidth, column.minWidth, column.maxWidth);
      } else {
        next[column.key] = column.defaultWidth;
      }
    });
    return next;
  }, [normalizedColumns, storageKey]);

  const [columnWidths, setColumnWidths] = React.useState(buildInitialWidths);

  React.useEffect(() => {
    setColumnWidths((previous) => {
      const next = {};
      let changed = false;

      normalizedColumns.forEach((column) => {
        const existing = Number(previous?.[column.key]);
        const normalized = Number.isFinite(existing)
          ? clamp(existing, column.minWidth, column.maxWidth)
          : column.defaultWidth;
        next[column.key] = normalized;

        if (!Number.isFinite(existing) || existing !== normalized) {
          changed = true;
        }
      });

      if (Object.keys(previous || {}).length !== normalizedColumns.length) {
        changed = true;
      }

      return changed ? next : previous;
    });
  }, [normalizedColumns]);

  React.useEffect(() => {
    if (!storageKey || normalizedColumns.length === 0) {
      return;
    }
    const payload = {};
    normalizedColumns.forEach((column) => {
      payload[column.key] = columnWidths[column.key] ?? column.defaultWidth;
    });
    writeStoredWidths(storageKey, payload);
  }, [columnWidths, normalizedColumns, storageKey]);

  const beginDrag = React.useCallback((cursor, onMove) => {
    if (typeof window === 'undefined') {
      return;
    }

    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = cursor;

    const handleMove = (event) => {
      onMove(event);
    };

    const handleUp = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  }, []);

  const resizeColumnBy = React.useCallback(
    (key, delta) => {
      const column = columnsByKey.get(key);
      if (!column || column.resizable === false) {
        return;
      }
      setColumnWidths((previous) => {
        const current = Number.isFinite(previous?.[key])
          ? previous[key]
          : column.defaultWidth;
        return {
          ...previous,
          [key]: clamp(current + delta, column.minWidth, column.maxWidth),
        };
      });
    },
    [columnsByKey],
  );

  const resizeColumnTo = React.useCallback(
    (key, width) => {
      const column = columnsByKey.get(key);
      if (!column || column.resizable === false) {
        return;
      }
      setColumnWidths((previous) => ({
        ...previous,
        [key]: clamp(width, column.minWidth, column.maxWidth),
      }));
    },
    [columnsByKey],
  );

  const startColumnResizeBase = React.useCallback(
    (event, key) => {
      event.preventDefault();
      event.stopPropagation();

      const column = columnsByKey.get(key);
      if (!column || column.resizable === false) {
        return;
      }

      const startX = event.clientX;
      const startWidth = Number.isFinite(columnWidths[key])
        ? columnWidths[key]
        : column.defaultWidth;

      beginDrag('col-resize', (moveEvent) => {
        const nextWidth = startWidth + (moveEvent.clientX - startX);
        setColumnWidths((previous) => ({
          ...previous,
          [key]: clamp(nextWidth, column.minWidth, column.maxWidth),
        }));
      });
    },
    [beginDrag, columnWidths, columnsByKey],
  );

  const startColumnResize = React.useMemo(
    () =>
      Object.assign(
        (event, key) => startColumnResizeBase(event, key),
        {
          resizeBy: resizeColumnBy,
          resizeTo: resizeColumnTo,
        },
      ),
    [resizeColumnBy, resizeColumnTo, startColumnResizeBase],
  );

  const getWidth = React.useCallback(
    (key) => {
      const column = columnsByKey.get(key);
      if (!column) {
        return DEFAULT_WIDTH;
      }
      const value = Number(columnWidths[key]);
      if (!Number.isFinite(value)) {
        return column.defaultWidth;
      }
      return clamp(value, column.minWidth, column.maxWidth);
    },
    [columnWidths, columnsByKey],
  );

  const tableMinWidth = React.useMemo(
    () =>
      normalizedColumns.reduce(
        (sum, column) => sum + getWidth(column.key),
        0,
      ),
    [normalizedColumns, getWidth],
  );

  return {
    columnWidths,
    getWidth,
    setColumnWidths,
    startColumnResize,
    resizeColumnBy,
    resizeColumnTo,
    tableMinWidth,
  };
}

export function ResizableColGroup({ columns, getWidth, leadingWidth = 0 }) {
  if (!Array.isArray(columns) || columns.length === 0) {
    return null;
  }

  return (
    <colgroup>
      {leadingWidth > 0 ? <col style={{ width: leadingWidth }} /> : null}
      {columns.map((column) => (
        <col key={column.key} style={{ width: getWidth(column.key) }} />
      ))}
    </colgroup>
  );
}

export function ResizableHeaderCell({
  column,
  getWidth,
  onResizeStart,
  children,
  sx,
  ...tableCellProps
}) {
  const width = getWidth(column.key);
  const resizable =
    column.resizable !== false && typeof onResizeStart === 'function';
  const resizeStep = 12;

  const handleResizeKeyDown = (event) => {
    if (!resizable) {
      return;
    }
    const columnMin = column.minWidth ?? DEFAULT_MIN_WIDTH;
    const columnMax = column.maxWidth ?? DEFAULT_MAX_WIDTH;
    const step = event.shiftKey ? resizeStep * 4 : resizeStep;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        onResizeStart.resizeBy?.(column.key, -step);
        break;
      case 'ArrowRight':
        event.preventDefault();
        onResizeStart.resizeBy?.(column.key, step);
        break;
      case 'Home':
        event.preventDefault();
        onResizeStart.resizeTo?.(column.key, columnMin);
        break;
      case 'End':
        event.preventDefault();
        onResizeStart.resizeTo?.(column.key, columnMax);
        break;
      default:
        break;
    }
  };

  return (
    <TableCell
      {...tableCellProps}
      sx={[
        {
          width,
          minWidth: width,
          maxWidth: width,
          position: 'relative',
          whiteSpace: 'nowrap',
          pr: resizable ? 2 : undefined,
        },
        sx,
      ]}
    >
      {children ?? column.label}
      {resizable ? (
        <Box
          role="separator"
          tabIndex={0}
          aria-label={`Resize ${column.label || column.key} column`}
          aria-orientation="vertical"
          aria-valuemin={column.minWidth ?? DEFAULT_MIN_WIDTH}
          aria-valuemax={column.maxWidth ?? DEFAULT_MAX_WIDTH}
          aria-valuenow={Math.round(width)}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => onResizeStart(event, column.key)}
          onKeyDown={handleResizeKeyDown}
          sx={{
            position: 'absolute',
            top: 0,
            right: -4,
            bottom: 0,
            width: 8,
            cursor: 'col-resize',
            zIndex: 3,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            '&:focus-visible': {
              backgroundColor: 'action.focus',
              outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              outlineOffset: -2,
            },
          }}
        />
      ) : null}
    </TableCell>
  );
}

export function TableLoadingRow({ colSpan, label = 'Loading...' }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan}>
        <Stack direction="row" spacing={1} alignItems="center" role="status" aria-live="polite">
          <CircularProgress size={20} aria-label={label} />
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

export function TableEmptyRow({ colSpan, label = 'No records found.' }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan}>
        <Typography variant="body2" color="text.secondary" role="status">
          {label}
        </Typography>
      </TableCell>
    </TableRow>
  );
}
